'use client';

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

type Size = 'sm' | 'md' | 'lg';

// Every size stays at 16px text on purpose: iOS Safari auto-zooms the page
// whenever a focused input's font-size is below 16px, and that zoom is not
// undone on blur. `sm` shrinks via height and padding, never type size.
const SIZE: Record<Size, string> = {
  sm: 'h-[var(--field-height-sm)] text-base',
  md: 'h-[var(--field-height)] text-base',
  lg: 'h-[var(--field-height)] text-base',
};

const PADDING: Record<Size, string> = {
  sm: 'px-3.5',
  md: 'px-4',
  lg: 'px-4',
};

// ─── Field shell — shared by Input + Textarea + Select wrappers ────

function fieldClasses({
  size,
  invalid,
  hasPrefix,
  hasSuffix,
}: {
  size: Size;
  invalid: boolean;
  hasPrefix: boolean;
  hasSuffix: boolean;
}): string {
  return [
    // `sf-field` is the hook globals.css uses to repaint browser autofill in
    // storefront colours. It must not be dropped.
    'sf-field',
    'block w-full rounded-[var(--radius-field)] border bg-background text-foreground',
    'placeholder:text-muted-foreground',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    SIZE[size],
    hasPrefix ? 'ps-11' : PADDING[size],
    hasSuffix ? 'pe-11' : PADDING[size],
    invalid
      ? 'border-danger focus-visible:border-danger focus-visible:ring-danger/30'
      : 'border-border hover:border-foreground/40',
  ]
    .filter(Boolean)
    .join(' ');
}

// ─── Field — wrapper that adds label + error message + the field itself ──

/**
 * The ARIA a control needs to match what the field is already showing.
 * Spread it onto the control — `{(id, _d, aria) => <Input id={id} {...aria} />}`
 * — rather than wiring each attribute by hand.
 */
export type FieldAria = {
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
};

type FieldProps = {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: (
    controlId: string,
    describedBy: string | undefined,
    aria: FieldAria,
  ) => ReactNode;
};

export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const aria: FieldAria = {
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    'aria-required': required || undefined,
  };

  // Explicit margins rather than one `space-y` rule: a label needs more air
  // above the field than a hint needs below it, and a single uniform gap
  // can't express that. 8px above / 6px below.
  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-sm font-medium text-foreground"
        >
          {label}
          {required && <span className="ms-1 text-danger" aria-hidden="true">*</span>}
        </label>
      )}
      {children(id, describedBy, aria)}
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {/* role="alert" so a message that appears after a failed submit is
          announced — otherwise the red text is the only signal. */}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Input ─────────────────────────────────────────────────────────

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> & {
  size?: Size;
  invalid?: boolean;
  /** Inline element rendered inside the start of the field (icon, text, currency). */
  prefix?: ReactNode;
  /** Inline element rendered inside the end of the field (icon, button). */
  suffix?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid = false, prefix, suffix, className, ...rest },
  ref,
) {
  if (!prefix && !suffix) {
    return (
      <input
        ref={ref}
        // Ahead of {...rest} so an explicit aria-invalid still wins.
        aria-invalid={invalid || undefined}
        className={[
          fieldClasses({ size, invalid, hasPrefix: false, hasSuffix: false }),
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
    );
  }
  return (
    <div className="relative">
      {prefix && (
        <span className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none text-muted-foreground">
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={[
          fieldClasses({ size, invalid, hasPrefix: !!prefix, hasSuffix: !!suffix }),
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      />
      {suffix && (
        <span className="absolute inset-y-0 end-0 flex items-center pe-4 text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
});

// ─── Textarea ──────────────────────────────────────────────────────

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  invalid?: boolean;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid = false, className, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={[
        'sf-field',
        'block w-full min-h-[7rem] rounded-[var(--radius-field)] border bg-background text-foreground text-base',
        'placeholder:text-muted-foreground transition-colors px-4 py-3',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary',
        'disabled:opacity-50 disabled:cursor-not-allowed resize-none',
        invalid
          ? 'border-danger focus-visible:border-danger focus-visible:ring-danger/30'
          : 'border-border hover:border-foreground/40',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  );
});
